import React, { useState, useRef, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import axios from "axios";
import { getHostUrl } from "../../../Redux/ApiCalls/getHostUrl";
import { countryCodes } from "../../../Utils/countryCodes";
import { showErrorToast, showSuccessToast } from "../../atoms/Utlis/Toast";
import { Phone, ChevronDown, User, Mail, ArrowRight, CheckCircle, X, Briefcase } from "lucide-react";
import { useAppDispatch } from "../../../Redux/Hooks";
import { setToken } from "../../../Redux/Slice/Auth/AuthSlice";
import { getMarketplaceProfile } from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import sendFirebaseEvent from "../../../analytics/FirebaseAnalytics";
import { isProfileCompleted } from "./MarketplaceShared";

const gradient = "linear-gradient(144.01deg, #ffc951 0%, #c94daa 130%)";

type Step = "phone" | "otp" | "register" | "success";

const sendOTP = (phone_number: string, country_code: string) =>
  axios.post(`${getHostUrl()}/account/sendotp_v2/`, { phone_number, country_code });

const verifyOTPApi = (phone_number: string, country_code: string, otp: string, orderId: string) =>
  axios.post(`${getHostUrl()}/account/login_v2/`, { phone_number, country_code, otp, orderId });

const registerApi = (payload: {
  name: string;
  phone_number: string;
  country_code: string;
  temp_token: string;
  role: number;
}) => axios.post(`${getHostUrl()}/account/marketplace/register/`, payload);

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(24px); }
  to   { opacity: 1; transform: translateX(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(201, 77, 170, 0.4); }
  50%       { box-shadow: 0 0 0 10px rgba(201, 77, 170, 0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

const checkPop = keyframes`
  0%   { transform: scale(0.5); opacity: 0; }
  70%  { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: ${fadeIn} 0.2s ease;
  backdrop-filter: blur(4px);
`;

const ModalCloseBtn = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--mp-headingColor, rgba(23, 23, 23, 0.6));
  transition: all 0.2s;
  z-index: 10;
  &:hover {
    opacity: 0.8;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 440px;
  background: var(--mp-cardBackground, white);
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  overflow: visible;
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  position: relative;
`;

const CardTopBarContainer = styled.div`
  height: 24px;
  overflow: hidden;
  border-top-left-radius: 23px;
  border-top-right-radius: 23px;
  margin-bottom: -19px;
  position: relative;
`;

const CardTopBar = styled.div`
  height: 5px;
  background: var(--mp-heroBtnBg, ${gradient});
`;

const CardBody = styled.div`
  padding: 36px 36px 32px;

  @media (max-width: 480px) {
    padding: 28px 24px 24px;
  }
`;

const StepContent = styled.div`
  animation: ${slideIn} 0.3s ease both;
`;

const Heading = styled.h1`
  margin: 0 0 8px;
  font-size: 26px;
  font-weight: 800;
  color: var(--mp-headingColor, #171717);
  line-height: 1.2;
`;

const Sub = styled.p`
  margin: 0 0 28px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.55));
  line-height: 1.6;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
`;

const FieldLabel = styled.label`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.55));
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const InputWrap = styled.div<{ $focused?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0;
  border: 1.5px solid ${p => p.$focused ? "var(--mp-heroAccent, #c94daa)" : "var(--mp-border, rgba(23,23,23,0.14))"};
  border-radius: 12px;
  background: var(--mp-cardBackground, #fafafa);
  transition: border-color 0.18s, box-shadow 0.18s;
  box-shadow: ${p => p.$focused ? "0 0 0 3px rgba(37, 99, 235, 0.15)" : "none"};
  overflow: hidden;
`;

const CountryCodeBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 12px;
  height: 48px;
  background: transparent;
  border: none;
  border-right: 1.5px solid var(--mp-border, rgba(23, 23, 23, 0.1));
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
  transition: background 0.15s;

  &:hover { opacity: 0.85; }
`;

const PhoneInput = styled.input`
  flex: 1;
  height: 48px;
  padding: 0 14px;
  border: none;
  outline: none;
  background: transparent;
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: var(--mp-headingColor, #171717);
  min-width: 0;

  &::placeholder { color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.35)); font-weight: 500; }
`;

const TextInput = styled.input`
  flex: 1;
  height: 48px;
  padding: 0 14px 0 10px;
  border: none;
  outline: none;
  background: transparent;
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 600;
  color: var(--mp-headingColor, #171717);
  min-width: 0;

  &::placeholder { color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.35)); font-weight: 500; }
`;

const InputIcon = styled.div`
  padding-left: 14px;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.35));
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const DropdownWrap = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 280px;
  max-height: 250px;
  background: var(--mp-cardBackground, white);
  border: 1px solid var(--mp-border, rgba(23, 23, 23, 0.1));
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
  z-index: 100;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} 0.15s ease;
`;

const DropdownSearch = styled.input`
  padding: 10px 14px;
  border: none;
  border-bottom: 1px solid var(--mp-border, rgba(23, 23, 23, 0.08));
  outline: none;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  background: var(--mp-cardBackground, #fafafa);
  color: var(--mp-headingColor, #171717);

  &::placeholder { color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.35)); }
`;

const DropdownList = styled.div`
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--mp-border, rgba(23,23,23,0.2)); border-radius: 2px; }
`;

const DropdownItem = styled.div<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  cursor: pointer;
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  color: var(--mp-headingColor, #171717);
  background: ${p => p.$active ? "var(--mp-border, rgba(201,77,170,0.08))" : "transparent"};
  transition: background 0.12s;

  &:hover { opacity: 0.85; }
`;

const DropdownCode = styled.span`
  font-weight: 700;
  color: var(--mp-heroSubColor, #c94daa);
  font-size: 12px;
  margin-left: auto;
`;

const OtpRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
  margin-bottom: 20px;

  @media (max-width: 380px) {
    gap: 6px;
  }
`;

const OtpBox = styled.input<{ $filled: boolean }>`
  width: 52px;
  height: 56px;
  border: 2px solid ${p => p.$filled ? "var(--mp-heroAccent, #c94daa)" : "var(--mp-border, rgba(23,23,23,0.14))"};
  border-radius: 12px;
  text-align: center;
  font-size: 22px;
  font-weight: 800;
  font-family: Urbanist, sans-serif;
  color: var(--mp-headingColor, #171717);
  background: ${p => p.$filled ? "var(--mp-border, rgba(201,77,170,0.05))" : "var(--mp-cardBackground, #fafafa)"};
  outline: none;
  transition: border-color 0.18s, background 0.18s;

  &:focus {
    border-color: var(--mp-heroAccent, #c94daa);
    animation: ${pulse} 1.5s ease infinite;
  }

  @media (max-width: 380px) {
    width: 44px;
    height: 48px;
    font-size: 18px;
  }
`;

const SubmitBtn = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  height: 50px;
  border: none;
  border-radius: 13px;
  background: var(--mp-heroBtnBg, ${gradient});
  color: var(--mp-heroBtnColor, white);
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: ${p => p.$disabled ? "not-allowed" : "pointer"};
  opacity: ${p => p.$disabled ? 0.6 : 1};
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
  transition: opacity 0.18s, transform 0.18s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;

  &:hover:not([disabled]) {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2.5px solid rgba(255,255,255,0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const ResendRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 16px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.5));
`;

const ResendLink = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.$active ? "var(--mp-heroSubColor, #c94daa)" : "var(--mp-border, rgba(23,23,23,0.3))"};
  cursor: ${p => p.$active ? "pointer" : "default"};
  text-decoration: ${p => p.$active ? "underline" : "none"};
  transition: color 0.15s;
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
`;

const StepDot = styled.div<{ $active: boolean; $done: boolean }>`
  width: ${p => p.$active ? "24px" : "8px"};
  height: 8px;
  border-radius: 100px;
  background: ${p => p.$done ? "#16a34a" : p.$active ? "var(--mp-heroBtnBg, " + gradient + ")" : "var(--mp-border, rgba(23,23,23,0.12))"};
  transition: all 0.3s ease;
`;

const BackStepBtn = styled.button`
  background: none;
  border: none;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.45));
  cursor: pointer;
  padding: 0;
  margin-top: 12px;
  display: block;
  text-align: center;
  width: 100%;

  &:hover { color: var(--mp-heroAccent, #c94daa); }
`;

const PhonePreview = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 100px;
  background: var(--mp-border, rgba(23, 23, 23, 0.05));
  font-family: Urbanist, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--mp-headingColor, rgba(23, 23, 23, 0.65));
  margin-bottom: 20px;
`;

const SuccessWrap = styled.div`
  text-align: center;
  padding: 20px 0 8px;
  animation: ${slideIn} 0.4s ease;
`;

const SuccessCheckIcon = styled.div`
  animation: ${checkPop} 0.5s ease;
  display: inline-block;
  margin-bottom: 16px;
`;

const SuccessTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 800;
  color: var(--mp-headingColor, #171717);
`;

const SuccessDesc = styled.p`
  margin: 0 0 24px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.55));
  line-height: 1.6;
`;

const RoleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 8px;
  margin-top: 4px;
`;

const RoleOptionCard = styled.div<{ $active: boolean }>`
  position: relative;
  border: ${p => p.$active ? "2px solid var(--mp-heroAccent, #c94daa)" : "1.5px solid var(--mp-border, rgba(128, 128, 128, 0.2))"};
  border-radius: 12px;
  padding: 14px;
  text-align: center;
  cursor: pointer;
  background: ${p => p.$active ? "rgba(201, 77, 170, 0.12)" : "rgba(128, 128, 128, 0.08)"};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  opacity: ${p => p.$active ? "1" : "0.75"};
  box-shadow: ${p => p.$active ? "0 4px 12px rgba(201, 77, 170, 0.15)" : "none"};

  &:hover {
    border-color: var(--mp-heroAccent, #c94daa);
    opacity: 1;
  }
`;

const RadioCircle = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid ${p => p.$active ? "var(--mp-heroAccent, #c94daa)" : "var(--mp-heroSubColor, rgba(128, 128, 128, 0.4))"};
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
`;

const RadioInnerDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--mp-heroAccent, #c94daa);
`;

const RoleName = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: var(--mp-headingColor, #171717);
`;

const RoleDesc = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  color: var(--mp-heroSubColor, rgba(23, 23, 23, 0.55));
  line-height: 1.3;
`;

const MarketplaceLoginModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleOpen = () => {
      setIsOpen(true);
      sendFirebaseEvent('Marketplace_Login_Modal_Opened', 'Marketplace', 'login_modal_opened', 'all');
    };
    window.addEventListener("open-marketplace-login", handleOpen);
    return () => window.removeEventListener("open-marketplace-login", handleOpen);
  }, []);

  const [step, setStep] = useState<Step>("phone");
  const [loading, setLoading] = useState(false);

  const [countryCode, setCountryCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [ddOpen, setDdOpen] = useState(false);
  const [ddSearch, setDdSearch] = useState("");
  const ddRef = useRef<HTMLDivElement>(null);
  const [phoneFocused, setPhoneFocused] = useState(false);

  const [orderId, setOrderId] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const [tempToken, setTempToken] = useState("");
  const [name, setName] = useState("");
  const [selectedRole, setSelectedRole] = useState<5 | 7>(5);
  const [nameFocused, setNameFocused] = useState(false);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ddRef.current && !ddRef.current.contains(e.target as Node)) {
        setDdOpen(false);
        setDdSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (step !== "otp") return;
    setTimer(60);
    setCanResend(false);
    const interval = setInterval(() => {
      setTimer(t => {
        if (t <= 1) {
          clearInterval(interval);
          setCanResend(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [step, orderId]);

  const filteredCodes = countryCodes.filter(
    c =>
      c.name.toLowerCase().includes(ddSearch.toLowerCase()) ||
      c.code.includes(ddSearch)
  );

  const isPhoneValid = phone.length === 10;
  const isOtpComplete = otp.every(d => d !== "");
  const isRegisterValid = name.trim().length > 1 && (selectedRole === 7 || selectedRole === 5);

  const resetState = () => {
    setStep("phone");
    setPhone("");
    setOtp(["", "", "", ""]);
    setName("");
    setSelectedRole(5);
  };

  const handleClose = () => {
    setIsOpen(false);
    resetState();
  };

  const handleSuccess = (isNewRegistration = false) => {
    setStep("success");
    setTimeout(() => {
      handleClose();
      window.dispatchEvent(new CustomEvent("marketplace-auth-change"));
      if (isNewRegistration) {
        // New user: go straight to profile creation to complete their profile
        localStorage.setItem("marketplace_profile_skipped", "true");
        localStorage.removeItem("marketplace_profile_created");
        window.location.href = "/marketplace/profile/create";
      } else {
        // Existing user: check and update profile cache
        localStorage.removeItem("marketplace_profile_skipped");
        getMarketplaceProfile()
          .then((res) => {
            const profileData = res?.data;
            if (profileData && isProfileCompleted(profileData)) {
              localStorage.setItem("marketplace_profile_created", "true");
              localStorage.setItem("marketplace_profile", JSON.stringify(profileData));
              window.dispatchEvent(new CustomEvent("marketplace-auth-change"));
            } else {
              localStorage.removeItem("marketplace_profile");
            }
          })
          .catch(() => {
            localStorage.removeItem("marketplace_profile");
          });
      }
    }, 1000);
  };

  const handleSendOTP = async () => {
    if (!isPhoneValid || loading) return;
    setLoading(true);
    try {
      const fullPhone = `${countryCode}${phone}`;
      const res = await sendOTP(fullPhone, countryCode);
      const data = res.data?.data;
      setOrderId(data?.orderId || data?.order_id || "");
      setStep("otp");
      showSuccessToast("OTP sent successfully!");
      sendFirebaseEvent('Marketplace_Login_OTP_Sent', 'Marketplace', 'otp_sent', `${countryCode}${phone}`);
    } catch (err: any) {
      const errMsg = err?.response?.data?.message || "Failed to send OTP.";
      showErrorToast(errMsg);
      sendFirebaseEvent('Marketplace_Login_Error', 'Marketplace', 'otp_send_error', errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend || loading) return;
    setLoading(true);
    try {
      const res = await axios.post(`${getHostUrl()}/account/resent-otp_v2/`, {
        orderId,
        country_code: countryCode,
      });
      setOrderId(res.data?.data?.orderId || orderId);
      setCanResend(false);
      setTimer(60);
      showSuccessToast("OTP resent!");
      sendFirebaseEvent('Marketplace_Login_OTP_Resent', 'Marketplace', 'otp_resent', `${countryCode}${phone}`);
    } catch (err: any) {
      showErrorToast("Failed to resend OTP.");
      sendFirebaseEvent('Marketplace_Login_Error', 'Marketplace', 'otp_resend_error', "Failed to resend OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!isOtpComplete || loading) return;
    setLoading(true);
    try {
      const res = await verifyOTPApi(phone, countryCode, otp.join(""), orderId);
      const data = res.data?.data;
      if (data.user_exists && data.user) {
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem("refresh_token", data.refresh_token);
        localStorage.setItem("user_info", JSON.stringify(data.user));
        dispatch(setToken({ access_token: data.access_token, refresh_token: data.refresh_token }));
        sendFirebaseEvent('Marketplace_Login_Success', 'Marketplace', 'login_success', 'all');
        // Existing user login — check if profile is complete
        handleSuccess(false);
      } else {
        setTempToken(data.temp_token);
        setStep("register");
        sendFirebaseEvent('Marketplace_Registration_Started', 'Marketplace', 'registration_started', 'all');
      }
    } catch (err: any) {
      const errMsg = err?.response?.data?.message || "Invalid OTP.";
      showErrorToast(errMsg);
      sendFirebaseEvent('Marketplace_Login_Error', 'Marketplace', 'otp_verify_error', errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!isRegisterValid || loading) return;
    setLoading(true);
    try {
      const res = await registerApi({
        name: name.trim(),
        phone_number: phone,
        country_code: countryCode,
        temp_token: tempToken,
        role: selectedRole,
      });
      const data = res.data?.data;
      localStorage.setItem("access_token", data.access_token);
      localStorage.setItem("refresh_token", data.refresh_token);
      localStorage.setItem("user_info", JSON.stringify(data.user));
      dispatch(setToken({ access_token: data.access_token, refresh_token: data.refresh_token }));
      showSuccessToast("Successfully registered! Complete your profile or explore the marketplace.");
      sendFirebaseEvent('Marketplace_Registration_Success', 'Marketplace', 'registration_success', 'all');
      handleSuccess(true); // new user → go to profile creation
    } catch (err: any) {
      const errMsg = err?.response?.data?.message || "Registration failed.";
      showErrorToast(errMsg);
      sendFirebaseEvent('Marketplace_Login_Error', 'Marketplace', 'registration_error', errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (val: string, idx: number) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[idx] = val;
    setOtp(next);
    if (val && idx < 3) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === "Backspace" && otp[idx] === "" && idx > 0) {
      otpRefs.current[idx - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    const next = ["", "", "", ""];
    text.split("").forEach((c, i) => { next[i] = c; });
    setOtp(next);
    otpRefs.current[Math.min(text.length, 3)]?.focus();
  };

  if (!isOpen) return null;

  const stepIndex = step === "phone" ? 0 : step === "otp" ? 1 : step === "register" ? 2 : 3;

  return (
    <ModalOverlay onClick={handleClose}>
      <Card onClick={e => e.stopPropagation()}>
        <ModalCloseBtn onClick={handleClose}><X size={18} /></ModalCloseBtn>
        <CardTopBarContainer>
          <CardTopBar />
        </CardTopBarContainer>
        <CardBody>
          {step !== "success" && (
            <StepIndicator>
              {[0, 1, 2].map(i => (
                <StepDot key={i} $active={stepIndex === i} $done={stepIndex > i} />
              ))}
            </StepIndicator>
          )}

          {step === "phone" && (
            <StepContent>
              <Heading>Welcome</Heading>
              <Sub>Enter your phone number to continue to the marketplace. We'll send you a quick OTP.</Sub>
              <FieldGroup>
                <FieldLabel>Phone Number</FieldLabel>
                <div style={{ position: "relative" }} ref={ddRef}>
                  <InputWrap $focused={phoneFocused}>
                    <CountryCodeBtn type="button" onClick={() => { setDdOpen(v => !v); setDdSearch(""); }}>
                      {countryCode}
                      <ChevronDown size={13} style={{ marginLeft: 2 }} />
                    </CountryCodeBtn>
                    <PhoneInput
                      id="mp-phone"
                      type="tel"
                      maxLength={10}
                      placeholder="Enter phone number"
                      value={phone}
                      onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      onFocus={() => setPhoneFocused(true)}
                      onBlur={() => setPhoneFocused(false)}
                      onKeyDown={e => e.key === "Enter" && handleSendOTP()}
                      autoFocus
                    />
                  </InputWrap>
                  {ddOpen && (
                    <DropdownWrap>
                      <DropdownSearch placeholder="Search country..." value={ddSearch} onChange={e => setDdSearch(e.target.value)} autoFocus />
                      <DropdownList>
                        {filteredCodes.map(c => (
                          <DropdownItem key={`${c.name}-${c.code}`} $active={countryCode === c.code} onClick={() => { setCountryCode(c.code); setDdOpen(false); setDdSearch(""); }}>
                            <span>{c.flag}</span>
                            <span style={{ flex: 1, fontFamily: "Urbanist, sans-serif", fontSize: 13 }}>{c.name}</span>
                            <DropdownCode>{c.code}</DropdownCode>
                          </DropdownItem>
                        ))}
                      </DropdownList>
                    </DropdownWrap>
                  )}
                </div>
              </FieldGroup>
              <SubmitBtn type="button" $disabled={!isPhoneValid || loading} disabled={!isPhoneValid || loading} onClick={handleSendOTP}>
                {loading ? <Spinner /> : <>Send OTP <ArrowRight size={16} /></>}
              </SubmitBtn>
              <p style={{ textAlign: "center", fontFamily: "Montserrat, sans-serif", fontSize: 11, color: "rgba(23,23,23,0.35)", marginTop: 16, lineHeight: 1.6 }}>
                By continuing, you agree to our Terms of Use and Privacy Policy.
              </p>
            </StepContent>
          )}

          {step === "otp" && (
            <StepContent>
              <Heading>Enter OTP 🔑</Heading>
              <Sub style={{ marginBottom: 16 }}>We've sent a 4-digit code to</Sub>
              <PhonePreview>
                <Phone size={13} />
                {countryCode} {phone}
              </PhonePreview>
              <OtpRow onPaste={handleOtpPaste}>
                {otp.map((digit, i) => (
                  <OtpBox
                    key={i}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    $filled={digit !== ""}
                    ref={el => { otpRefs.current[i] = el; }}
                    onChange={e => handleOtpChange(e.target.value, i)}
                    onKeyDown={e => handleOtpKeyDown(e, i)}
                    autoFocus={i === 0}
                  />
                ))}
              </OtpRow>
              <ResendRow>
                {canResend ? (
                  <ResendLink $active={true} onClick={handleResendOTP}>Resend OTP</ResendLink>
                ) : (
                  <>Resend in&nbsp;<ResendLink $active={false}>{String(Math.floor(timer / 60)).padStart(2, "0")}:{String(timer % 60).padStart(2, "0")}</ResendLink></>
                )}
              </ResendRow>
              <SubmitBtn type="button" $disabled={!isOtpComplete || loading} disabled={!isOtpComplete || loading} onClick={handleVerifyOTP}>
                {loading ? <Spinner /> : <>Verify OTP <ArrowRight size={16} /></>}
              </SubmitBtn>
              <BackStepBtn onClick={() => { setStep("phone"); setOtp(["","","",""]); }}>← Change phone number</BackStepBtn>
            </StepContent>
          )}

          {step === "register" && (
            <StepContent>
              <Heading>Create Account ✨</Heading>
              <Sub>Just a few more details to get you started on the marketplace.</Sub>
              <FieldGroup>
                <FieldLabel>Full Name</FieldLabel>
                <InputWrap $focused={nameFocused}>
                  <InputIcon><User size={15} /></InputIcon>
                  <TextInput id="mp-name" type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} onFocus={() => setNameFocused(true)} onBlur={() => setNameFocused(false)} onKeyDown={e => e.key === "Enter" && handleRegister()} autoFocus />
                </InputWrap>
              </FieldGroup>
              <FieldGroup>
                <FieldLabel>Register As</FieldLabel>
                <RoleGrid>
                  <RoleOptionCard
                    $active={selectedRole === 5}
                    onClick={() => setSelectedRole(5)}
                  >
                    <RadioCircle $active={selectedRole === 5}>
                      {selectedRole === 5 && <RadioInnerDot />}
                    </RadioCircle>
                    <User size={20} color={selectedRole === 5 ? "var(--mp-heroAccent, #c94daa)" : "var(--mp-heroSubColor, rgba(128, 128, 128, 0.5))"} />
                    <RoleName>Freelancer</RoleName>
                    <RoleDesc>Photographer, Editor, Drone pilot...</RoleDesc>
                  </RoleOptionCard>
                  <RoleOptionCard
                    $active={selectedRole === 7}
                    onClick={() => setSelectedRole(7)}
                  >
                    <RadioCircle $active={selectedRole === 7}>
                      {selectedRole === 7 && <RadioInnerDot />}
                    </RadioCircle>
                    <Briefcase size={20} color={selectedRole === 7 ? "var(--mp-heroAccent, #c94daa)" : "var(--mp-heroSubColor, rgba(128, 128, 128, 0.5))"} />
                    <RoleName>Business</RoleName>
                    <RoleDesc>Production team or rental house...</RoleDesc>
                  </RoleOptionCard>
                </RoleGrid>
              </FieldGroup>
              <SubmitBtn type="button" $disabled={!isRegisterValid || loading} disabled={!isRegisterValid || loading} onClick={handleRegister}>
                {loading ? <Spinner /> : <>Create Account <ArrowRight size={16} /></>}
              </SubmitBtn>
              <BackStepBtn onClick={() => setStep("otp")}>← Back to OTP</BackStepBtn>
            </StepContent>
          )}

          {step === "success" && (
            <SuccessWrap>
              <SuccessCheckIcon>
                <CheckCircle size={56} color="#16a34a" strokeWidth={1.5} />
              </SuccessCheckIcon>
              <SuccessTitle>You're in! 🎉</SuccessTitle>
              <SuccessDesc>Logged in successfully. Refreshing the page...</SuccessDesc>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Spinner style={{ borderColor: "rgba(22,163,74,0.3)", borderTopColor: "#16a34a" } as any} />
              </div>
            </SuccessWrap>
          )}
        </CardBody>
      </Card>
    </ModalOverlay>
  );
};

export default MarketplaceLoginModal;
